import { getReferenceString } from '@medplum/core';
import { Coverage, Organization } from '@medplum/fhirtypes';
import { InsuranceSource } from './insurance-eligibility';
import { PN_POLICYHOLDER_OPTIONS, PnPolicyholder } from './pn-consultation-script';
import {
  COVERAGE_CLASS_CODESYSTEM,
  COVERAGE_SOURCE_CODESYSTEM,
  GROUP_CLASS_CODE,
  CoverageSource,
  INSURANCE_PAYER_KEY_IDENTIFIER_SYSTEM,
  SUBSCRIBER_RELATIONSHIP_CODESYSTEM,
} from './insurance-coverage';

/**
 * What the PN card's insurance section prefills from signup (BAR-2919): how
 * the patient gets insurance, the insurer and whose plan, and the member ID
 * and group number (BAR-2920). Read from the
 * Coverage bariendo-be wrote at signup, falling back to the eligibility
 * form's `coverage-source` and `insurer` answers. Pure, so the card only
 * fetches.
 */

const SOURCE_BY_CODE: Partial<Record<CoverageSource, InsuranceSource>> = {
  job: 'Through a job',
  self: 'Bought it myself',
  tricare: 'Tricare',
  va: 'VA',
  medicare: 'Medicare',
  medicaid: 'Medicaid',
};

/** The eligibility form stores the option's label, not its code (bariendo-be `optionLabel`). */
const SOURCE_BY_SIGNUP_LABEL: Record<string, InsuranceSource> = {
  'Through a job': 'Through a job',
  'I bought it myself': 'Bought it myself',
  Tricare: 'Tricare',
  VA: 'VA',
  Medicare: 'Medicare',
  Medicaid: 'Medicaid',
};

/** `Coverage.relationship` is the patient's relationship to the subscriber: a child is on a parent's plan. */
const [THEIRS, SPOUSE_OR_PARTNER, PARENT] = PN_POLICYHOLDER_OPTIONS;
const POLICYHOLDER_BY_RELATIONSHIP: Record<string, PnPolicyholder> = {
  self: THEIRS,
  spouse: SPOUSE_OR_PARTNER,
  child: PARENT,
};

/** Signup's "Mine isn't listed" answer: its key, as the session stores it. */
const SIGNUP_INSURER_NOT_LISTED_KEY = '__not_listed__';

/**
 * Whether signup's insurer answer was "Mine isn't listed": the eligibility
 * form stores its label (bariendo-be's `INSURER_NOT_LISTED` option), or the
 * key when a label wasn't resolved. Either apostrophe.
 */
export function isSignupInsurerNotListed(name: string | undefined): boolean {
  if (!name) return false;
  return (
    name === SIGNUP_INSURER_NOT_LISTED_KEY || name.replace('\u2019', "'").trim().toLowerCase() === "mine isn't listed"
  );
}

export interface InsurancePrefill {
  source?: InsuranceSource;
  insurer?: { name: string; reference: string; key: string };
  /** The insurer's name from the form, when there is no payor Organization to point at. */
  insurerName?: string;
  policyholder?: PnPolicyholder;
  /** The card details signup took, when the patient entered them (BAR-2920). */
  memberId?: string;
  groupNumber?: string;
}

/** The Coverage source code for the card's answer (BAR-2922), for the `update-insurance-coverage` bot. */
export function coverageSourceCode(source: string | undefined): CoverageSource | undefined {
  return (Object.entries(SOURCE_BY_CODE) as [CoverageSource, InsuranceSource][]).find(
    ([, label]) => label === source
  )?.[0];
}

export function payerKey(organization: Organization | undefined): string | undefined {
  return organization?.identifier?.find((entry) => entry.system === INSURANCE_PAYER_KEY_IDENTIFIER_SYSTEM)?.value;
}

/**
 * The prefill from the signup Coverage and its payor Organization, then the
 * eligibility form's labels for anything the Coverage lacks. "Something else"
 * and a payor that is the Patient ("not listed") prefill nothing.
 */
export function readInsurancePrefill(
  coverage: Coverage | undefined,
  payor: Organization | undefined,
  signup: { source?: string; insurer?: string } = {}
): InsurancePrefill {
  const code = coverage?.type?.coding?.find((coding) => coding.system === COVERAGE_SOURCE_CODESYSTEM)?.code;
  const source =
    (code ? SOURCE_BY_CODE[code as CoverageSource] : undefined) ??
    // The form stores the label; a coded answer reads through the codes.
    (signup.source
      ? (SOURCE_BY_SIGNUP_LABEL[signup.source] ?? SOURCE_BY_CODE[signup.source as CoverageSource])
      : undefined);

  const relationship = coverage?.relationship?.coding?.find(
    (coding) => coding.system === SUBSCRIBER_RELATIONSHIP_CODESYSTEM
  )?.code;

  // Only a payor with a payer key, as the insurer list offers: the key decides
  // the criteria. Without one, the name is still matched against the list.
  const reference = payor?.id ? getReferenceString(payor) : undefined;
  const key = payerKey(payor);
  return {
    source,
    insurer: reference && payor?.name && key ? { name: payor.name, reference, key } : undefined,
    insurerName: payor?.name ?? signup.insurer,
    policyholder: relationship ? POLICYHOLDER_BY_RELATIONSHIP[relationship] : undefined,
    memberId: coverage?.subscriberId?.trim() || undefined,
    groupNumber:
      coverage?.class
        ?.find((entry) =>
          entry.type?.coding?.some(
            (coding) => coding.system === COVERAGE_CLASS_CODESYSTEM && coding.code === GROUP_CLASS_CODE
          )
        )
        ?.value?.trim() || undefined,
  };
}
